import { useState } from 'react';
// import { Link } from 'react-router-dom';
import { Trash2, Eye, Printer, AlertTriangle, Copy, MoreVertical, PlusCircle } from 'lucide-react';
import type { TenantPrinterWithDetails } from '@maker-toolchest/shared';
import { SearchInput } from '@/components/SearchInput';
import { Select } from '@/components/Select';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DropdownMenu } from '@/components/DropdownMenu';
import { FilterBar } from '@/components/FilterBar';
import { useTenantPrinters, useRemovePrinterFromLibrary } from '../hooks';
import type { TenantPrinterQuery } from '../api';
import { PrinterStatusBadge } from './PrinterStatusBadge';

interface MyPrintersListProps {
  onViewPrinter?: (printer: TenantPrinterWithDetails) => void;
  onClonePrinter?: (printer: TenantPrinterWithDetails) => void;
}

export function MyPrintersList({ onViewPrinter, onClonePrinter }: MyPrintersListProps) {
  const [filters, setFilters] = useState<TenantPrinterQuery>({
    page: 1,
    limit: 20,
    sortBy: 'instanceName',
    sortOrder: 'asc',
  });
  const [deleteTarget, setDeleteTarget] = useState<TenantPrinterWithDetails | null>(null);
  const [searchKey, setSearchKey] = useState(0);

  const { data, isLoading } = useTenantPrinters(filters, {
    placeholderData: (previousData) => previousData,
  });
  const removeMutation = useRemovePrinterFromLibrary();

  const printers = data || [];

  // Extract unique brands and models from the user's actual printers
  const userBrands = Array.from(
    new Map(
      printers
        .filter((tp) => tp.printer.brand)
        .map((tp) => [tp.printer.brand!.id, tp.printer.brand!])
    ).values()
  );

  const userModels = Array.from(
    new Map(
      printers
        .filter((tp) => tp.printer.model)
        .map((tp) => [tp.printer.model!.id, tp.printer.model!])
    ).values()
  );

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }));
  };

  const handleFilterChange = (key: keyof TenantPrinterQuery, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    try {
      await removeMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'instanceName',
      sortOrder: 'asc',
    });
  };

  const handleApplyFilters = (newFilters: Record<string, any>) => {
    // Set all filters including search
    setFilters(prev => ({
      ...prev,
      search: newFilters.search || undefined,
      brandId: newFilters.brandId || undefined,
      modelId: newFilters.modelId || undefined,
      status: newFilters.status || undefined,
      sortBy: newFilters.sortBy || prev.sortBy,
      page: 1,
    }));

    // Force remount of SearchInput to clear stale debounced state
    setSearchKey(prev => prev + 1);
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.brandId ||
    filters.modelId ||
    filters.status
  );

  // Build options for selects - only show brands/models that exist in user's printers
  const brandOptions = [
    { value: '', label: 'All Brands' },
    ...userBrands.map((brand) => ({ value: brand.id, label: brand.name })),
  ];

  const modelOptions = [
    { value: '', label: 'All Models' },
    ...userModels.map((model) => ({ value: model.id, label: model.name })),
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'IN_SERVICE', label: 'In Service' },
    { value: 'IN_STORAGE', label: 'In Storage' },
    { value: 'NEEDS_MAINTENANCE', label: 'Needs Maintenance' },
    { value: 'OUT_OF_SERVICE', label: 'Out of Service' },
    { value: 'DISPOSED', label: 'Disposed' },
  ];

  const sortByOptions = [
    { value: 'addedAt', label: 'Date Added' },
    { value: 'instanceName', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'totalPrintHours', label: 'Total Hours' },
  ];

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <FilterBar
        searchField={
          <SearchInput
            key={searchKey}
            value={filters.search || ''}
            onChange={handleSearch}
            placeholder="Search your printers..."
          />
        }
        filterFields={[
          <div className="w-40">
            <Select
              label="Brand"
              value={filters.brandId || ''}
              onChange={(e) => handleFilterChange('brandId', e.target.value)}
              options={brandOptions}
            />
          </div>,
          <div className="w-48">
            <Select
              label="Model"
              value={filters.modelId || ''}
              onChange={(e) => handleFilterChange('modelId', e.target.value)}
              options={modelOptions}
            />
          </div>,
          <div className="w-40">
            <Select
              label="Status"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              options={statusOptions}
            />
          </div>,
          <div className="w-36">
            <Select
              label="Sort By"
              value={filters.sortBy || 'instanceName'}
              onChange={(e) => handleFilterChange('sortBy', e.target.value as TenantPrinterQuery['sortBy'])}
              options={sortByOptions}
            />
          </div>,
        ]}
        filterConfigs={[
          { key: 'brandId', label: 'Brand', type: 'select', options: brandOptions },
          { key: 'modelId', label: 'Model', type: 'select', options: modelOptions },
          { key: 'status', label: 'Status', type: 'select', options: statusOptions },
          { key: 'sortBy', label: 'Sort By', type: 'select', options: sortByOptions },
        ]}
        filterValues={{
          search: filters.search || '',
          brandId: filters.brandId || '',
          modelId: filters.modelId || '',
          status: filters.status || '',
          sortBy: filters.sortBy || 'instanceName',
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Results */}
      {printers.length === 0 ? (
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-foreground-muted">
            {filters.search || filters.brandId || filters.modelId || filters.status
              ? 'No printers found matching your criteria.'
              : "You haven't added any printers yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Printer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Model
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Usage
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-background">
                {printers.map((tp) => (
                  <tr key={tp.id} className="hover:bg-background-subtle/50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-subtle">
                          <Printer className="h-5 w-5 text-foreground-muted" />
                        </div>
                        <div>
                          {onViewPrinter ? (
                            <button
                              onClick={() => onViewPrinter(tp)}
                              className="text-left text-sm font-medium text-foreground hover:text-primary transition-colors"
                            >
                              {tp.instanceName || tp.printer.name}
                            </button>
                          ) : (
                            <div className="text-sm font-medium text-foreground">
                              {tp.instanceName || tp.printer.name}
                            </div>
                          )}
                          {tp.serialNumber && (
                            <div className="text-xs text-foreground-muted">
                              SN: {tp.serialNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                      {tp.printer.brand?.name || 'Custom'} {tp.printer.model?.name || 'Build'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <PrinterStatusBadge status={tp.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {tp.totalPrintHours > 0 || tp.totalJobCount > 0 ? (
                        <div className="text-foreground-muted">
                          {tp.totalPrintHours.toFixed(1)}h · {tp.totalJobCount} jobs
                        </div>
                      ) : (
                        <span className="flex items-center gap-1.5 text-warning">
                          <AlertTriangle className="h-4 w-4" />
                          <span>No usage</span>
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex items-center justify-end">
                        <DropdownMenu
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Actions"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          }
                          items={[
                            ...(onViewPrinter
                              ? [
                                  {
                                    label: 'View',
                                    icon: <Eye className="h-4 w-4" />,
                                    onClick: () => onViewPrinter(tp),
                                  },
                                ]
                              : []),
                            {
                              label: 'Quick Add Job',
                              icon: <PlusCircle className="h-4 w-4" />,
                              onClick: () => {
                                // TODO: Implement quick add job functionality
                              },
                              disabled: true,
                            },
                            ...(onClonePrinter
                              ? [
                                  {
                                    label: 'Clone',
                                    icon: <Copy className="h-4 w-4" />,
                                    onClick: () => onClonePrinter(tp),
                                  },
                                ]
                              : []),
                            {
                              label: 'Delete',
                              icon: <Trash2 className="h-4 w-4" />,
                              onClick: () => setDeleteTarget(tp),
                              variant: 'danger' as const,
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TODO: Pagination support */}
          {/* {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
            />
          )} */}
        </>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleRemove}
        title="Remove Printer"
        description={`Are you sure you want to remove "${deleteTarget?.instanceName || deleteTarget?.printer.name}" from your library?`}
        confirmText="Remove"
        variant="danger"
        loading={removeMutation.isPending}
      />
    </div>
  );
}
